/* @jsx h */
import { computed, defineComponent, h, onMounted, ref, type Ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { api } from "@app/data/api";
import { useAuth, useTrpc, isGroupSession } from "@app/vue/providers/index";
import { useLocalization, type SchemaFormInstance } from "@lemmonhq/uikit";
import {
  getLocales,
  loadCountries,
  loadCurrencies,
  loadTimezones,
} from "../ManagerSettings/subpages/ProfileSettings/helpers/countryData";
import type {
  CountryOption,
  CurrencyOption,
  TimezoneOption,
} from "../ManagerSettings/subpages/ProfileSettings/types";
import { SignupSidebar } from "./SignupSidebar";
import { SignupFormPanel } from "./SignupFormPanel";
import { BusinessTypePicker } from "./BusinessTypePicker";
import { safeSignupReturnTo } from "@app/utils/signup";
import {
  createInitialForm,
  createStepOptions,
  createSteps,
  createStepSchemas,
  getSmartCountryDefaults,
} from "./signupConfig";
import type { SignupForm } from "./signupTypes";

export const SignupPage = defineComponent({
  name: "SignupPage",
  setup() {
    const router = useRouter();
    const route = useRoute();
    // Set when an AI client's MCP connect flow sent the user here: after signup
    // they go straight back to the consent screen instead of the dashboard.
    const returnTo = safeSignupReturnTo(
      route.query.returnTo,
      window.location.origin,
    );
    const trpc = useTrpc();
    const auth = useAuth();
    const { t } = useLocalization();

    const formRef = ref<SchemaFormInstance | null>(null);
    const form = ref<SignupForm>(createInitialForm());
    const currentStep = ref(0);
    const error = ref<string | null>(null);
    const submitting = ref(false);
    const countries: Ref<CountryOption[]> = ref([]);
    const loadingCountries = ref(true);
    const timezones: Ref<TimezoneOption[]> = ref([]);
    const loadingTimezones = ref(true);
    const currencies: Ref<CurrencyOption[]> = ref([]);
    const loadingCurrencies = ref(true);
    const locales = getLocales();
    // Set once the owner picks a country themselves, so detection never overrides it.
    const regionTouched = ref(false);

    // Cloudflare IP geolocation first, then the region in the browser locale (e.g. de-CH).
    const detectCountry = async () => {
      const fromServer = await trpc.signup.detectRegion
        .query()
        .then((result) => result.country)
        .catch(() => null);
      if (fromServer) return fromServer;
      const region = navigator.language?.split("-")[1];
      return region?.length === 2 ? region.toUpperCase() : null;
    };

    onMounted(async () => {
      const [loadedCountries, loadedTimezones, loadedCurrencies, detected] =
        await Promise.all([
          loadCountries(),
          loadTimezones(),
          loadCurrencies(),
          detectCountry(),
        ]);
      countries.value = loadedCountries;
      loadingCountries.value = false;
      timezones.value = loadedTimezones;
      loadingTimezones.value = false;
      currencies.value = loadedCurrencies;
      loadingCurrencies.value = false;
      if (
        detected &&
        !regionTouched.value &&
        loadedCountries.some((country) => country.value === detected)
      ) {
        applyCountryDefaults(detected);
      }
    });

    const steps = computed(() => createSteps(t));
    const stepSchemas = computed(() => createStepSchemas(t));
    const activeSchema = computed(() => stepSchemas.value[currentStep.value]);
    const stepOptions = computed(() =>
      createStepOptions({
        t,
        // Reads the form at render time, so picking a type doesn't rebuild the options.
        renderBusinessType: () => (
          <BusinessTypePicker
            value={form.value.restaurantType}
            onChange={(restaurantType: string) =>
              (form.value = { ...form.value, restaurantType })
            }
          />
        ),
        countries: countries.value,
        loadingCountries: loadingCountries.value,
        locales,
        timezones: timezones.value,
        loadingTimezones: loadingTimezones.value,
        currencies: currencies.value,
        loadingCurrencies: loadingCurrencies.value,
      }),
    );
    const activeOptions = computed(() => stepOptions.value[currentStep.value]);
    const countryLabel = computed(() => {
      const country = countries.value.find(
        (option) => option.value === form.value.jurisdiction,
      );
      return country
        ? `${country.flag ? `${country.flag} ` : ""}${country.label}`
        : form.value.jurisdiction;
    });

    const applyCountryDefaults = (jurisdiction: string) => {
      const defaults = getSmartCountryDefaults(jurisdiction);
      const country = countries.value.find(
        (option) => option.value === jurisdiction,
      );
      form.value = {
        ...form.value,
        jurisdiction,
        locale: defaults.locale,
        timezone: defaults.timezone,
        currency: defaults.currency || country?.currency || form.value.currency,
      };
    };

    const handleFormChange = (value: SignupForm) => {
      if (
        value.jurisdiction &&
        value.jurisdiction !== form.value.jurisdiction
      ) {
        regionTouched.value = true;
        applyCountryDefaults(value.jurisdiction);
      } else {
        form.value = { ...form.value, ...value };
      }
      error.value = null;
    };

    const validateCurrentStep = () => {
      error.value = null;
      const value = formRef.value?.getValue();
      if (value) return true;

      const result = activeSchema.value.safeParse(form.value);
      error.value = result.success
        ? t("signup.page.required")
        : result.error.issues[0]?.message || t("signup.page.required");
      return false;
    };

    const goToStep = (index: number) => {
      error.value = null;
      currentStep.value = Math.max(0, Math.min(index, steps.value.length - 1));
    };

    const submit = async (event: Event) => {
      event.preventDefault();
      if (!validateCurrentStep()) return;
      if (currentStep.value < steps.value.length - 1) {
        goToStep(currentStep.value + 1);
        return;
      }

      submitting.value = true;
      error.value = null;

      try {
        const createdUser = await trpc.signup.create.mutate({
          restaurantName: form.value.restaurantName.trim(),
          restaurantType: form.value.restaurantType,
          ownerName: form.value.ownerName.trim(),
          ownerEmail: form.value.ownerEmail.trim(),
          phone: form.value.phone.trim(),
          address: form.value.address.trim(),
          companyRegistration: form.value.companyRegistration.trim() || null,
          jurisdiction: form.value.jurisdiction,
          locale: form.value.locale,
          timezone: form.value.timezone,
          currency: form.value.currency,
          password: form.value.password,
          confirmPassword: form.value.confirmPassword,
          acceptedTerms: true,
        });
        const session = await api.getSession();
        // Signup always yields a POS-mode owner session; guard the union anyway.
        auth.setUser(
          !session || isGroupSession(session) ? createdUser : session,
        );
        if (returnTo) {
          // A full navigation, not router.push: the authorize endpoint is
          // server-rendered, and starting it from this page makes it same-site,
          // so the new SameSite=Strict session cookie is sent along.
          window.location.assign(returnTo);
          return;
        }
        await router.push("/app/reports");
      } catch (nextError) {
        error.value =
          nextError instanceof Error
            ? nextError.message
            : t("signup.page.create_failed");
      } finally {
        submitting.value = false;
      }
    };

    return () => (
      <div class="flex min-h-0 flex-1 bg-base-200 text-base-content">
        <SignupSidebar
          steps={steps.value}
          currentStep={currentStep.value}
          onSelectStep={goToStep}
        />
        <main class="flex min-w-0 flex-1 flex-col overflow-y-auto">
          <SignupFormPanel
            form={form.value}
            steps={steps.value}
            currentStep={currentStep.value}
            activeSchema={activeSchema.value}
            activeOptions={activeOptions.value}
            countryLabel={countryLabel.value}
            error={error.value}
            submitting={submitting.value}
            onFormRef={(instance) => (formRef.value = instance)}
            onSubmit={submit}
            onFormChange={handleFormChange}
            onBack={() => goToStep(currentStep.value - 1)}
            onGoToStep={goToStep}
          />
        </main>
      </div>
    );
  },
});
